import React, { PropsWithChildren, useEffect, useRef } from "react";
import { generateClassName } from "../../../hooks/useAttributes";
import IElementProps from "../../../types/ElementProps";
import { getTextColorFromBackground } from "../../../util/util";
import Icon from "../icons/Icon";
import "./Button.css";

export interface ButtonBase {
  text?: string,
  icon?: string,
  color?: string,
  size?: "small" | "regular" | "tiny" | "large",
  variant?: "solid" | "subtle" | "ghost",
}

export interface Clickable {
  onClick?: () => (any | Promise<any>);
  loading?: boolean | undefined;
  disabled?: boolean;
}

interface Props extends IElementProps, ButtonBase, Clickable {
  type?: "button" | "submit" | "reset",
  disabledText?: string,
  iconSize?: number;
  iconPosition?: "start" | "end",
  loadingText?: string
}

export default function Button(props: PropsWithChildren<Props>) {

  const {
    className,
    iconPosition = "start",
    onClick,
    color,
    disabled,
    iconSize,
    loading: externalLoading,
    variant = "solid",
    text,
    type = "button",
    loadingText,
    children,
    icon,
    style,
    size = "regular"
  } = props;

  const [loading, setLoading] = React.useState<boolean>(false);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false }, []);

  const clickHandler = async () => {
    setLoading(true);
    await onClick?.();
    if (mounted.current) setLoading(false);
  }

  const setColor = style?.backgroundColor ?? color ?? "var(--primaryAccent)";

  const getFgColor = () => {
    switch (variant) {
      case "ghost":
      case "subtle":
        return setColor;
      case "solid":
        return getTextColorFromBackground(setColor, setColor.includes("var"), "#000000")
    }
  }

  const isSubmit = type === "submit";

  const buttonClass = generateClassName(className, `transition button position-relative button-${variant} button-size-${size}`, {
    value: isSubmit,
    onTrue: "align-self-end"
  });


  const isLoading = loading || externalLoading;

  const iconComponent = (
    icon && (
      <Icon
        loading={isLoading}
        icon={icon}
        size={iconSize || (size === "small" ? 10 : 12)}
      />
    )
  )

  return (
    <button
      type={type}
      onClick={clickHandler}
      className={buttonClass}
      style={{
        color: getFgColor(),
      }}
      disabled={isLoading || disabled}
    >
      <div
        className="transition button-background w-100 h-100 position-absolute top-0 start-0"
        style={{
          backgroundColor: setColor,
        }}
      />
      <div className="button-content position-relative gap-1 d-flex flex-row align-items-center justify-content-center">
        {
          iconPosition === "start" && iconComponent
        }
        {
          children || ((text || isLoading) && (
            <span
              className={generateClassName("button-text text-nowrap", { value: loading, onTrue: "ms-2" })}
            >{isLoading ? (loadingText || "Loading...") : text}</span>)
          )
        }
        {
          iconPosition === "end" && iconComponent
        }
      </div>
    </button>
  )
}